import { useState } from 'react'

import {
  useTagCandidateApproveApi,
  useTagCandidateCreateApi,
  useTagCandidateExtractApi,
  useTagCandidateHoldApi,
  useTagCandidateListApi,
  useTagCandidatePreviewApi,
  useTagCandidateRejectApi
} from '@/common/api/modules/searchTag'
import { TagCandidate, TagCandidateStatus } from '@/common/api/params/searchTag'
import { useSiteId } from '@/common/app/site'

import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import Tab from '@/components/view/atoms/tab/Tab'
import TabList from '@/components/view/atoms/tab/TabList'
import Table from '@/components/view/atoms/table/Table'
import TableHeadItem from '@/components/view/atoms/table/TableHeadItem'
import TableRow from '@/components/view/atoms/table/TableRow'
import TableRowItem from '@/components/view/atoms/table/TableRowItem'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import Area from '@/components/view/layouts/area/Area'
import AreaContent from '@/components/view/layouts/area/AreaContent'
import Content from '@/components/view/layouts/page/Content'
import Main, { MainLayoutType } from '@/components/view/layouts/page/Main'
import PageTitle from '@/components/view/molecules/title/PageTitle'

const STATUS_TABS = [
  { key: 'pending', label: '承認待ち' },
  { key: 'approved', label: '承認済み' },
  { key: 'held', label: '保留' },
  { key: 'rejected', label: '却下' }
] as const

const SOURCE_LABELS: Record<string, string> = {
  search: '検索ワード',
  product_text: '商品テキスト',
  structured: '構造化情報',
  manual: '手動追加'
}

// 構造化情報の候補は、どの情報から出た語かを出す（スペックはスペック種別も添える）
const REF_TYPE_LABELS: Record<string, string> = {
  category: 'カテゴリ',
  brand: 'ブランド',
  product_spec: 'スペック',
  keyword: '検索キーワード'
}

const sourceLabel = (c: {
  source: string
  source_ref_type: string | null
  source_ref_id: string | null
}): string => {
  const base = SOURCE_LABELS[c.source] ?? c.source
  if (c.source !== 'structured' || !c.source_ref_type) return base
  const ref = REF_TYPE_LABELS[c.source_ref_type] ?? c.source_ref_type
  return c.source_ref_type === 'product_spec' && c.source_ref_id
    ? `${ref}（${c.source_ref_id}）`
    : ref
}

const textInput =
  'shadow-inset text-size-13 rounded-lg border border-border-input bg-input px-3 py-2'

/**
 * 見込み母集団の表示。
 * 未算出（null）を 0 と混同させないため、「試算」ボタンを出す。
 */
const PopulationCell = ({
  candidate,
  bandMin,
  bandMax,
  onPreview,
  previewing
}: {
  candidate: TagCandidate
  bandMin: number
  bandMax: number
  onPreview: () => void
  previewing: boolean
}) => {
  if (candidate.preview_population === null) {
    return (
      <Button color={ButtonColor.SECONDARY} onClick={onPreview} disabled={previewing}>
        {previewing ? '試算中...' : '試算する'}
      </Button>
    )
  }
  const n = candidate.preview_population
  const inBand = n >= bandMin && n <= bandMax
  return (
    <Text color={inBand ? TextColor.SUCCESS : TextColor.SECONDARY}>
      {n} 件{inBand ? '' : `（帯 ${bandMin}〜${bandMax} 外）`}
    </Text>
  )
}

const SearchTagCandidatesPage = () => {
  const siteId = useSiteId()
  const [status, setStatus] = useState<TagCandidateStatus>('pending')
  const [search, setSearch] = useState('')
  const [keyword, setKeyword] = useState('')
  const [newWord, setNewWord] = useState('')
  const [previewingId, setPreviewingId] = useState<number | null>(null)
  // 実行中の「候補ID＋操作」。SWRの isMutating はフック単位（＝表の全行で共有）なので、
  // それで disabled を決めると押していない行のボタンまで反応してしまう。
  // 操作まで持つのは、押したボタン自身にスピナーを出すため
  const [acting, setActing] = useState<{
    id: number
    action: 'approve' | 'hold' | 'reject'
  } | null>(null)
  const [message, setMessage] = useState('')

  const { data, mutate, isLoading } = useTagCandidateListApi({
    site_id: siteId,
    status,
    search: keyword || undefined,
    per_page: 50
  })

  const reload = () => mutate()

  const { trigger: preview } = useTagCandidatePreviewApi({
    onSuccess: (res) => {
      setPreviewingId(null)
      if (!res.measurable) {
        setMessage('意味検索が使えないため試算できませんでした（ベクトル未投入の可能性）')
      }
      reload()
    },
    // 失敗時に戻さないと、その行が「試算中...」のまま固まる
    onError: () => setPreviewingId(null)
  })
  const { trigger: approve } = useTagCandidateApproveApi({
    onSuccess: (res) => {
      setActing(null)
      setMessage(
        `「${res.tag.tag_name}」を承認しました（slug: ${res.tag.tag_slug} / 母集団 ${res.tag.population}件）` +
          (res.converged ? '' : ` — ${res.note}`)
      )
      reload()
    },
    onError: () => setActing(null)
  })
  const { trigger: reject } = useTagCandidateRejectApi({
    onSuccess: (res) => {
      setActing(null)
      setMessage(`「${res.word}」を却下しました`)
      reload()
    },
    onError: () => setActing(null)
  })
  const { trigger: hold } = useTagCandidateHoldApi({
    onSuccess: (res) => {
      setActing(null)
      setMessage(`「${res.word}」を保留にしました`)
      reload()
    },
    onError: () => setActing(null)
  })
  const { trigger: create } = useTagCandidateCreateApi({
    onSuccess: () => {
      setNewWord('')
      setMessage('候補を追加しました')
      reload()
    }
  })
  const { trigger: extract, isMutating: extracting } = useTagCandidateExtractApi({
    onSuccess: (res) => {
      setMessage(
        `候補抽出：新規 ${res.created} 件 / 更新 ${res.updated} 件` +
          `（検索 ${res.source_search} / 商品テキスト ${res.source_product_text} / 構造化 ${res.source_structured}）`
      )
      reload()
    }
  })

  // 帯は設定APIを叩かずタグ詳細と同じ既定値で表示する（設定画面で変更可）
  const bandMin = 10
  const bandMax = 40

  const counts = data?.counts_by_status ?? {}

  return (
    <Main type={MainLayoutType.FULL}>
      <PageTitle
        title='承認キュー'
        description='タグ候補を承認するとタグが作られ、母集団（紐づく商品）が即座に形成されます。'
        rightNode={
          <Button
            color={ButtonColor.SECONDARY}
            onClick={() => extract({ site_id: siteId })}
            disabled={extracting}
          >
            {extracting ? '抽出中...' : '🔄 候補を抽出'}
          </Button>
        }
      />
      <Content>
        {message && (
          <div className='bg-surface-emphasis rounded-lg p-3'>
            <Text size={12}>{message}</Text>
          </div>
        )}

        <TabList>
          {STATUS_TABS.map((t) => (
            <Tab key={t.key} selected={status === t.key} onClick={() => setStatus(t.key)}>
              {t.label}
              {counts[t.key] != null ? `（${counts[t.key]}）` : ''}
            </Tab>
          ))}
        </TabList>

        <Area>
          <AreaContent>
            <div className='mb-3 flex flex-wrap items-center gap-2'>
              <input
                className={textInput}
                placeholder='候補語で絞り込み'
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') setKeyword(search.trim())
                }}
              />
              <Button color={ButtonColor.SECONDARY} onClick={() => setKeyword(search.trim())}>
                検索
              </Button>
              <div className='ml-auto flex items-center gap-2'>
                <input
                  className={textInput}
                  placeholder='タグ候補を手動で追加'
                  value={newWord}
                  onChange={(e) => setNewWord(e.target.value)}
                />
                <Button
                  color={ButtonColor.PRIMARY}
                  onClick={() =>
                    newWord.trim() && create({ site_id: siteId, word: newWord.trim() })
                  }
                >
                  追加
                </Button>
              </div>
            </div>

            {isLoading ? (
              <Text color={TextColor.SECONDARY}>読み込み中...</Text>
            ) : !data || data.items.length === 0 ? (
              <Text color={TextColor.SECONDARY}>
                該当する候補がありません。「候補を抽出」で3源泉から集められます。
              </Text>
            ) : (
              <>
                <Text size={12} color={TextColor.SECONDARY}>
                  全 {data.total} 件（スコア順）
                </Text>
                <Table>
                  <thead>
                    <TableRow head>
                      <TableHeadItem>
                        <Text size={12}>候補語</Text>
                      </TableHeadItem>
                      <TableHeadItem>
                        <Text size={12}>源泉</Text>
                      </TableHeadItem>
                      <TableHeadItem>
                        <Text size={12}>検索数</Text>
                      </TableHeadItem>
                      <TableHeadItem>
                        <Text size={12}>出現商品数</Text>
                      </TableHeadItem>
                      <TableHeadItem>
                        <Text size={12}>見込み母集団（在庫あり）</Text>
                      </TableHeadItem>
                      <TableHeadItem>
                        <Text size={12}>スコア</Text>
                      </TableHeadItem>
                      <TableHeadItem>
                        <Text size={12}>操作</Text>
                      </TableHeadItem>
                    </TableRow>
                  </thead>
                  <tbody>
                    {data.items.map((c) => (
                      <TableRow key={c.id}>
                        <TableRowItem>{c.word}</TableRowItem>
                        <TableRowItem>{sourceLabel(c)}</TableRowItem>
                        <TableRowItem>{c.search_freq}</TableRowItem>
                        <TableRowItem>{c.product_count}</TableRowItem>
                        <TableRowItem>
                          <PopulationCell
                            candidate={c}
                            bandMin={bandMin}
                            bandMax={bandMax}
                            previewing={previewingId === c.id}
                            onPreview={() => {
                              setPreviewingId(c.id)
                              preview({ site_id: siteId, candidate_id: c.id })
                            }}
                          />
                        </TableRowItem>
                        <TableRowItem>{c.score.toFixed(2)}</TableRowItem>
                        <TableRowItem>
                          {c.status === 'pending' ? (
                            <div className='flex gap-1'>
                              <Button
                                color={ButtonColor.PRIMARY}
                                loading={acting?.id === c.id && acting.action === 'approve'}
                                disabled={acting?.id === c.id}
                                onClick={() => {
                                  setActing({ id: c.id, action: 'approve' })
                                  approve({ site_id: siteId, candidate_id: c.id })
                                }}
                              >
                                承認
                              </Button>
                              <Button
                                color={ButtonColor.SECONDARY}
                                loading={acting?.id === c.id && acting.action === 'hold'}
                                disabled={acting?.id === c.id}
                                onClick={() => {
                                  setActing({ id: c.id, action: 'hold' })
                                  hold({ site_id: siteId, candidate_id: c.id })
                                }}
                              >
                                保留
                              </Button>
                              <Button
                                color={ButtonColor.CRITICAL}
                                loading={acting?.id === c.id && acting.action === 'reject'}
                                disabled={acting?.id === c.id}
                                onClick={() => {
                                  setActing({ id: c.id, action: 'reject' })
                                  reject({ site_id: siteId, candidate_id: c.id })
                                }}
                              >
                                却下
                              </Button>
                            </div>
                          ) : (
                            <Text color={TextColor.SECONDARY}>—</Text>
                          )}
                        </TableRowItem>
                      </TableRow>
                    ))}
                  </tbody>
                </Table>
              </>
            )}
          </AreaContent>
        </Area>
      </Content>
    </Main>
  )
}

export default SearchTagCandidatesPage
